import { Injectable } from '@angular/core';
import { DatePipe } from "@angular/common";

@Injectable({
  providedIn: 'root'
})
export class DateTimeService {

  constructor(private datePipe: DatePipe) {

  }

  /* Previous implementation used moment.js. Moment.js uses Internationalization API to determine
  timezone information, so current formatting is using Internationalization API directly.
  The API timezone detection should be compatable with most browsers except Opera.
   */

  /* [abbr]-- accounting for Standard Time
     [dstabbr] -- accounting for Daylight Time
     (Some timezones do not have a Daylight Time)
   */
  public timezones = [
    {abbr:'AST', tz:'America/Puerto_Rico', dstabbr: null},
    {abbr:'EST', tz:'America/New_York', dstabbr: 'EDT'},
    {abbr:'CST', tz:'America/Chicago', dstabbr: 'CDT'},
    {abbr:'MST', tz:'America/Denver', dstabbr: 'MDT'},
    {abbr:'Arizona', tz:'America/Phoenix', dstabbr: null},
    {abbr:'PST', tz:'America/Los_Angeles', dstabbr: 'PDT'},
    {abbr:'AKST', tz:'America/Anchorage', dstabbr: 'AKDT'},
    {abbr:'HST', tz:'Pacific/Honolulu', dstabbr: null},
    {abbr:'SST', tz:'Pacific/Pago_Pago', dstabbr: 'GMT-11'},
    {abbr:'ChST', tz:'Pacific/Guam', dstabbr: 'GMT+10'}
  ]

  public intlFormatterOptions:any = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZone: "America/New_York"
  }

  public monthOrder = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];


  /*
  Date/Time format for Company Events calendar and page
   */
  public formatCompanyEventTimes(data:any){
    let timeVar = {
      startDate: data.start_date,
      endDate: data.end_date,
      utcStart: data.start_time,
      utcEnd: data.end_time,
      viewStart: "",
      viewEnd: "",
      outlookStart: "",
      outlookEnd: "",
      isAllDay: false,
      timezone: data.timezone
    }

    this.intlFormatterOptions.timeZone = timeVar.timezone !== null ? timeVar.timezone : "America/New_York";

    let baseAndUTCZonesOffset = (base:any, utc:any) => {
      let baseOffset = base.getTimezoneOffset();
      let utcOffset = utc.getTimezoneOffset();
      let status:string;
      if((baseOffset !== utcOffset) && (baseOffset > utcOffset)){
        status = 'back'
      } else if ((baseOffset !== utcOffset) && (baseOffset < utcOffset)){
        status = 'forward'
      } else {
        status = ''
      }
      return {
        isNotSame: baseOffset !== utcOffset,
        status: status
      }
    }

    if(!data.start_time){
      timeVar.isAllDay = true;
    }

    if(data.end_date && !data.end_time){
      timeVar.isAllDay = false;
    }

    if(data.start_time){
      let baseDate = data.start_date ? new Date(data.start_date) : new Date();
      let [startHour, startMin, startSec] = data.start_time.split(":");
      baseDate.setHours(startHour);
      baseDate.setMinutes(startMin);
      let utcBase = new Date(Date.UTC(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDay(), baseDate.getHours(), baseDate.getMinutes()));
      let dstCheck = baseAndUTCZonesOffset(baseDate, utcBase)
      if(dstCheck.isNotSame){
        utcBase.setHours(utcBase.getHours() + (dstCheck.status=='back' ? -1 : 1))
      }
      let intlUtcOffsetDate = new Intl.DateTimeFormat('en-US', this.intlFormatterOptions).format(utcBase);
      let tzOffsetDate = new Date(intlUtcOffsetDate);
      timeVar.viewStart = this.datePipe.transform(tzOffsetDate, "HH:mm:ss") ?? "";
    }

    if(data.end_time){
      let baseDate = data.end_date ? new Date(data.end_date) : new Date(data.start_date);
      let [endHour, endMin, endSec] = data.end_time.split(":");
      baseDate.setHours(endHour);
      baseDate.setMinutes(endMin);
      let utcBase = new Date(Date.UTC(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDay(), baseDate.getHours(), baseDate.getMinutes()));
      let dstCheck = baseAndUTCZonesOffset(baseDate, utcBase)
      if(dstCheck.isNotSame){
        utcBase.setHours(utcBase.getHours() + (dstCheck.status=='back' ? -1 : 1))
      }
      let intlUtcOffsetDate = new Intl.DateTimeFormat('en-US', this.intlFormatterOptions).format(utcBase);
      let tzOffsetDate = new Date(intlUtcOffsetDate);
      timeVar.viewEnd = this.datePipe.transform(tzOffsetDate, "HH:mm:ss") ?? "";
    }

    timeVar.outlookStart = this.datePipe.transform(data.start_date + " " + timeVar.viewStart, "yyyy-MM-ddTHH:mm:ss") ?? ""
    timeVar.outlookEnd = this.datePipe.transform((data.end_date!=null ? data.end_date : data.start_date) + " " + (timeVar.viewEnd.length ? timeVar.viewEnd : "23:59:00"), "yyyy-MM-ddTHH:mm:ss") ?? ""
    return timeVar;
  }

  public convertTimezoneToUTC(date:any, tz:any){
    if(tz==null) tz = this.getLocalTimezone().tz
    const inputDate = new Date(date)
    let utc = this.datePipe.transform(inputDate, "yyyy-MM-ddTHH:mm:ss", "UTC") ?? ""
    let utcDate = new Date(utc)
    if(tz === this.getLocalTimezone().tz){
      return this.datePipe.transform(utcDate, "HH:mm:ss")
    }
    const tzDate = new Date(inputDate.toLocaleString("en-US", {timeZone: tz}))
    let diff = (inputDate.getTime() - tzDate.getTime()) / (1000 * 60 * 60)
    utcDate.setHours(utcDate.getHours() + diff);
    return this.datePipe.transform(utcDate, "HH:mm:ss",)
  }

  formatUTCTime(date:any, tz:any){

    this.intlFormatterOptions.timeZone = tz.tz;
    let baseAndUTCZonesOffset = (base:any, utc:any) => {
      let baseOffset = base.getTimezoneOffset();
      let utcOffset = utc.getTimezoneOffset();
      let status:string;
      if((baseOffset !== utcOffset) && (baseOffset > utcOffset)){
        status = 'back'
      } else if ((baseOffset !== utcOffset) && (baseOffset < utcOffset)){
        status = 'forward'
      } else {
        status = ''
      }
      return {
        isNotSame: baseOffset !== utcOffset,
        status: status
      }
    }

    let baseDate = date;
    let utcBase = new Date(Date.UTC(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDay(), baseDate.getHours(), baseDate.getMinutes()));
    let dstCheck = baseAndUTCZonesOffset(baseDate, utcBase)
    if(dstCheck.isNotSame){
      utcBase.setHours(utcBase.getHours() + (dstCheck.status=='back' ? -1 : 1))
    }
    let intlUtcOffsetDate = new Intl.DateTimeFormat('en-US', this.intlFormatterOptions).format(utcBase);
    let tzOffsetDate = new Date(intlUtcOffsetDate);
    return this.datePipe.transform(tzOffsetDate, "h:mm a") ?? "";
  }

  public getLocalTimezone() {
    let dt = Intl.DateTimeFormat(undefined, {timeZoneName: 'short'}).formatToParts(new Date()).find(part => part.type == "timeZoneName")
    let localdt = dt != undefined ? dt.value : null
    let isArizDT: boolean = false;
    if (localdt == 'MST') {
      isArizDT = Intl.DateTimeFormat().resolvedOptions().timeZone == "America/Phoenix";
    }
    let timezoneValue = isArizDT ? this.timezones.filter((zone) => zone.tz == "America/Phoenix") : this.timezones.filter((zone) => zone.abbr == localdt || zone.dstabbr == localdt)
    return timezoneValue[0];
  }

  public companyEventDateTimePayload = (start:any, end:any, startTime:any, endTime:any, timezone:any, initial_date_time:number, id:string) => {
    return {
      "id": id,
      "start_date": start,
      "end_date": end,
      "start_time": startTime,
      "end_time": endTime,
      "initial_date_time": initial_date_time,
      "timezone": timezone
    }
  }

  public isDST(date = new Date()) {
    const janOffset = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
    const julOffset = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();

    return Math.max(janOffset, julOffset) !== date.getTimezoneOffset();
  }

  sortKeysByMonth(obj:any) {

    const sortedKeys = Object.keys(obj).sort((a, b) => {
      return this.monthOrder.indexOf(a) - this.monthOrder.indexOf(b);
    });

    const sortedObject:any = {};
    sortedKeys.forEach(key => {
      sortedObject[key] = obj[key];
    });

    return sortedObject;
  }

}
